import { Button } from "@wondermaps/ui";
import { VisitorDrawer } from "./VisitorDrawer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPersonWalking } from "@fortawesome/pro-solid-svg-icons/faPersonWalking";
import { faWheelchair } from "@fortawesome/pro-solid-svg-icons/faWheelchair";
import { faCheck } from "@fortawesome/pro-solid-svg-icons/faCheck";
import { faTag } from "@fortawesome/pro-solid-svg-icons/faTag";
import { useTranslation } from "react-i18next";
import type { Route } from "@wondermaps/maps";
import { formatDistance } from "../../lib/format-distance";
import { formatWalkTime } from "../../lib/walk-time";
import { contrastText } from "../../lib/color-utils";
import { findIconByName } from "../../data/fontawesome-icons";
import { POISheetHeader } from "./poi/POISheetHeader";

interface RoutePreviewCardProps {
  route: Route;
  destinationId: string;
  destinationName: string;
  categoryName?: string | null;
  categoryIcon?: string | null;
  onStart: () => void;
  /** X button — fully dismisses preview + cancels route */
  onClose: () => void;
  /** Cancel button — reopens POI detail sheet */
  onBackToPOI: () => void;
  accessibleMode?: boolean;
  overlayAccessible?: boolean;
  manualAccessibleMode?: boolean;
  isFallbackRoute?: boolean;
  onToggleAccessible?: () => void;
  brandAccent?: string | null;
  isBookmarked?: boolean;
  onBookmarkToggle?: (id: string) => void;
  bookmarksEnabled?: boolean;
}

export function RoutePreviewCard({
  route,
  destinationId,
  destinationName,
  categoryName,
  categoryIcon,
  onStart,
  onClose,
  onBackToPOI,
  accessibleMode,
  overlayAccessible,
  manualAccessibleMode,
  isFallbackRoute,
  onToggleAccessible,
  brandAccent,
  isBookmarked,
  onBookmarkToggle,
  bookmarksEnabled,
}: RoutePreviewCardProps) {
  const { t, i18n } = useTranslation("map");

  const distText = formatDistance(route.totalDistance, i18n.language, t);
  const walkTime = formatWalkTime(route.totalDistance, accessibleMode, t);

  const startBg = brandAccent ?? "#0d9488";
  const startColor = contrastText(startBg);

  const catIcon = categoryIcon ? findIconByName(categoryIcon) : null;

  return (
    <VisitorDrawer open modal={false} maxHeightClass="max-h-[40vh]">
      <div className="p-4 space-y-3">
        {/* Header */}
        <POISheetHeader
          title={destinationName}
          subtitle={[
            ...(categoryName ? [{ icon: catIcon?.icon ?? faTag, text: categoryName }] : []),
            ...(walkTime ? [{ icon: accessibleMode ? faWheelchair : faPersonWalking, text: `${walkTime} · ${distText}` }] : []),
          ]}
          isBookmarked={isBookmarked}
          onBookmarkToggle={onBookmarkToggle ? () => onBookmarkToggle(destinationId) : undefined}
          bookmarksEnabled={bookmarksEnabled}
          onClose={onClose}
        />

        {/* Accessible route toggle — full-card button, visual div checkbox (no nested button) */}
        {onToggleAccessible && (
          <button
            type="button"
            onClick={onToggleAccessible}
            disabled={overlayAccessible}
            className="w-full flex items-center gap-3 rounded-2xl border p-3 text-left"
            style={{
              borderColor: accessibleMode ? (brandAccent ?? "#0d9488") : undefined,
              backgroundColor: accessibleMode ? `${brandAccent ?? "#0d9488"}0d` : undefined,
            }}
          >
            {/* Visual checkbox — reflects manual state, not combined */}
            <div
              className="w-5 h-5 rounded border-2 flex items-center justify-center shrink-0"
              style={{
                borderColor: accessibleMode ? (brandAccent ?? "#0d9488") : undefined,
                backgroundColor: manualAccessibleMode ? (brandAccent ?? "#0d9488") : "transparent",
              }}
            >
              {manualAccessibleMode && (
                <FontAwesomeIcon icon={faCheck} className="h-3 w-3 text-white" />
              )}
            </div>
            <div>
              <p className="text-sm font-medium">{t("directions.accessibleRouteShort")}</p>
              {overlayAccessible
                ? <p className="text-xs text-muted-foreground">{t("directions.accessibleForcedByFilters")}</p>
                : <p className="text-xs text-muted-foreground">{t("directions.accessibleRouteSublabel")}</p>
              }
            </div>
          </button>
        )}

        {/* Warnings */}
        {isFallbackRoute && (
          <div className="bg-amber-100 text-amber-900 px-3 py-2 text-sm rounded-xl">
            {t("directions.noAccessibleRoute")}
          </div>
        )}
        {!accessibleMode && route.hasStairs && (
          <div className="bg-amber-100 text-amber-900 px-3 py-2 text-sm rounded-xl">
            {t("directions.routeIncludesStairs")}
          </div>
        )}
        {!accessibleMode && route.hasSteepGrade && (
          <div className="bg-amber-100 text-amber-900 px-3 py-2 text-sm rounded-xl">
            {t("directions.routeIncludesSteepGrades")}
          </div>
        )}

        {/* Actions — 30/70 split, pill-shaped */}
        <div className="flex gap-2">
          <Button variant="outline" className="w-[30%] rounded-2xl" onClick={onBackToPOI}>
            {t("common:cancel")}
          </Button>
          <button
            type="button"
            onClick={onStart}
            className="w-[70%] flex items-center justify-center rounded-2xl px-4 py-3 text-sm font-medium"
            style={{ backgroundColor: startBg, color: startColor }}
          >
            {t("directions.startNavigation")}
          </button>
        </div>
      </div>
    </VisitorDrawer>
  );
}
